import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Check, Crown, ArrowRight, Sparkles } from "lucide-react";

/*
 * Public /pricing page — linkable from marketing collateral. Non-authed
 * viewers land here first, get a clear picture of the money, then sign up.
 *
 * Feb 2026 catalog:
 *   Foundation ($49/mo) — invite-only, locked-in for founding members, 2 months free.
 *                         Not a self-serve signup CTA (routed to invite request).
 *   Pro         ($59/mo) — public default, 30-day free trial.
 *   Platinum    ($99/mo) — public premium, 30-day free trial, priority map
 *                          + search placement, white-glove onboarding, badge,
 *                          monthly performance reports.
 *
 * Pricing here is descriptive only — Stripe is the source of truth via
 * lookup_keys `regular_monthly` / `pro_monthly` / `platinum_monthly`. Any
 * price change must land in /app/backend/setup_stripe.py first.
 */

const CORE_FEATURES = [
  "Unlimited last-minute openings",
  "Real-time booking inbox",
  "Instant email alerts on every request",
  "Follower push notifications on new posts",
  "Marketing kit — QR poster + hours poster",
  "Multi-category profile + weekly hours",
  "Cancel any time — no lock-in",
];

const PRO_FEATURES = [
  "Everything in Foundation",
  "30-day free trial (no card charged upfront)",
  "Standard email support",
  "Available to all new businesses",
];

const PLATINUM_FEATURES = [
  "Everything in Pro",
  "Priority Map & Search placement",
  "White-Glove onboarding",
  "'Featured Business' badge",
  "Monthly Performance Reports",
];

const FOUNDATION_EXTRAS = [
  "Everything in Pro",
  "2 months free (no card charged upfront)",
  "Locked-in $49/mo pricing — for life",
  "Original member badge",
  "Direct line to the founding team",
];

function FoundationWaitlistForm() {
  const [email, setEmail] = useState("");
  const [city, setCity] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/foundation/waitlist", { email, city });
      setResult(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (result) {
    const msg =
      result.status === "available"
        ? `Good news, Foundation spots are still open in ${result.city}. Sign up with an invite code or grab one at checkout.`
        : result.status === "offered"
        ? `A spot just opened in ${result.city}, and it's held for you for 48 hours. Check your inbox.`
        : `You're #${result.position} on the ${result.city} waitlist. We'll email you when a spot opens.`;
    return (
      <p data-testid="pricing-waitlist-result" className="mt-4 border-l-2 border-[#C9A85F] pl-3 text-xs">
        {msg}
      </p>
    );
  }

  return (
    <form onSubmit={submit} data-testid="pricing-waitlist-form" className="mt-4 grid gap-2">
      <p className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
        City sold out? Join the waitlist
      </p>
      <input
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="you@business.com"
        data-testid="pricing-waitlist-email"
        className="h-10 border border-border bg-transparent px-3 text-sm outline-none focus:border-[#C9A85F]"
      />
      <input
        required
        minLength={2}
        value={city}
        onChange={(e) => setCity(e.target.value)}
        placeholder="City (e.g. Sydney)"
        data-testid="pricing-waitlist-city"
        className="h-10 border border-border bg-transparent px-3 text-sm outline-none focus:border-[#C9A85F]"
      />
      <button
        type="submit"
        disabled={busy}
        data-testid="pricing-waitlist-submit"
        className="h-10 border border-[#C9A85F] text-xs font-semibold uppercase tracking-widest text-[#8A7333] transition-colors hover:bg-[#C9A85F]/10 disabled:opacity-60"
      >
        {busy ? "Joining…" : "Join waitlist"}
      </button>
    </form>
  );
}

function FoundationSeatsSummary() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/foundation/availability").then(({ data }) => setData(data)).catch(() => setData(null));
  }, []);
  if (!data) return null;
  const top = data.cities.slice(0, 4);
  return (
    <div data-testid="pricing-foundation-seats" className="mt-6 border border-[#C9A85F]/50 p-4">
      <p className="mono text-[10px] uppercase tracking-widest text-[#8A7333]">
        Live · {data.seats_per_city} spots per city
      </p>
      <p data-testid="pricing-foundation-seats-total" className="mt-2 text-sm">
        {data.total_seats_used} founding member{data.total_seats_used === 1 ? "" : "s"} across{" "}
        {data.cities_with_members} cit{data.cities_with_members === 1 ? "y" : "ies"}
        {data.cities_full > 0 && ` · ${data.cities_full} sold out`}
      </p>
      {top.length > 0 && (
        <ul className="mt-3 grid gap-1">
          {top.map((c) => (
            <li key={c.city} className="mono flex justify-between text-[10px] uppercase tracking-widest text-muted-foreground">
              <span>{c.city}</span>
              <span className={c.is_full ? "text-destructive" : "text-foreground"}>
                {c.is_full ? "sold out" : `${c.seats_remaining} left`}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function Pricing() {
  return (
    <div className="min-h-[calc(100vh-56px)]" data-testid="pricing-page">
      {/* Hero */}
      <section className="border-b border-border">
        <div className="mx-auto max-w-[1200px] px-6 py-16 md:px-12 md:py-24">
          <p className="mono text-[10px] uppercase tracking-[0.3em] text-[#C9A85F]">
            → pricing
          </p>
          <h1 className="mt-6 text-5xl leading-[1.02] tracking-tight md:text-7xl">
            Fill more chairs.
            <br />
            <span className="slogan gold-metallic-text">Pay for the platform, not per booking.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-base text-muted-foreground md:text-lg">
            Flat monthly fee. No booking commissions. No per-transaction cuts.
            Every plan starts with a free trial (<span className="text-foreground font-semibold">30 days on Pro &amp; Platinum</span>,{" "}
            <span className="text-foreground font-semibold">2 months free for Foundation Members</span>) to
            onboard your existing customers. No card charged upfront.
          </p>
        </div>
      </section>

      {/* Tier grid — 3 columns on desktop */}
      <section className="mx-auto max-w-[1200px] px-6 py-16 md:px-12 md:py-24">
        <div className="grid gap-6 md:grid-cols-3">
          {/* Foundation — invite-only, per-city founding-member seat */}
          <TierCard
            testId="pricing-tier-foundation"
            eyebrow="Foundation"
            eyebrowIcon={<Crown size={11} className="text-[#C9A85F]" fill="currentColor" />}
            badge="invite only"
            price={49}
            priceNote="2 months free when you join as a Foundation member"
            tagline="Original members. Locked-in pricing for life. Not open to new signups."
            features={FOUNDATION_EXTRAS}
            ctaLabel="Enter invite code"
            ctaTo="/register"
            ctaTestId="pricing-foundation-cta"
            variant="foundation"
            extra={<><FoundationSeatsSummary /><FoundationWaitlistForm /></>}
            footNote={
              <>
                Foundation code required at signup ·{" "}
                <a href="mailto:hello@bookaway.com.au" className="underline decoration-[#C9A85F] underline-offset-2 text-foreground">
                  request one
                </a>
              </>
            }
          />

          {/* Pro — public default, highlighted "most popular" */}
          <TierCard
            testId="pricing-tier-pro"
            eyebrow="Pro"
            badge="most popular"
            badgeIcon={<Sparkles size={11} className="text-[#C9A85F]" />}
            price={59}
            tagline="For any salon, barber, brow bar, or clinic ready to fill same-day openings."
            features={[...PRO_FEATURES.slice(0, 2), ...CORE_FEATURES]}
            ctaLabel="Start 30-day free trial"
            ctaTo="/register"
            ctaTestId="pricing-pro-cta"
            variant="pro"
          />

          {/* Platinum — premium public tier */}
          <TierCard
            testId="pricing-tier-platinum"
            eyebrow="Platinum"
            eyebrowIcon={<Crown size={11} className="text-[#C9A85F]" fill="currentColor" />}
            price={99}
            tagline="Premium membership. Get seen first, get set up faster, and see how you're doing."
            features={PLATINUM_FEATURES}
            ctaLabel="Start 30-day free trial"
            ctaTo="/register"
            ctaTestId="pricing-platinum-cta"
            variant="platinum"
          />
        </div>

        {/* Fine print */}
        <div className="mt-16 border-t border-border pt-8 grid gap-6 md:grid-cols-3 mono text-[11px] uppercase tracking-widest text-muted-foreground">
          <div>
            <p className="text-[#C9A85F]">→ free trial</p>
            <p className="mt-2 text-muted-foreground normal-case tracking-normal text-sm">
              2 months free on Foundation, 30 days on Pro &amp; Platinum. No card charged upfront — you'll be
              prompted to add one before your trial ends. Cancel anytime.
            </p>
          </div>
          <div>
            <p className="text-[#C9A85F]">→ payments</p>
            <p className="mt-2 text-muted-foreground normal-case tracking-normal text-sm">
              Secure checkout via Stripe. GST included on your invoice.
              Cancel or update card from the Stripe customer portal.
            </p>
          </div>
          <div>
            <p className="text-[#C9A85F]">→ zero commissions</p>
            <p className="mt-2 text-muted-foreground normal-case tracking-normal text-sm">
              Book Away never takes a percentage of your bookings. Your
              customers pay you at the chair — we're not in that flow.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}

function TierCard({
  testId,
  eyebrow,
  eyebrowIcon,
  badge,
  badgeIcon,
  price,
  tagline,
  features,
  ctaLabel,
  ctaTo,
  ctaTestId,
  variant,
  extra,
  priceNote,
  footNote,
}) {
  const isFoundation = variant === "foundation";
  const isPro = variant === "pro";
  const isPlatinum = variant === "platinum";

  const containerCls = isFoundation
    ? "border border-[#C9A85F] bg-[#C9A85F]/5"
    : isPro
    ? "border-2 border-[#C9A85F] bg-[#C9A85F]/[0.03] md:scale-[1.02] shadow-[0_0_0_1px_rgba(201,168,95,0.15)]"
    : "border border-border";

  const eyebrowColor = isFoundation
    ? "text-[#8A7333]"
    : isPro
    ? "text-[#C9A85F]"
    : "text-muted-foreground";

  const ctaCls = isPro || isPlatinum
    ? "gold-metallic-bg text-black active:scale-[0.98]"
    : "border border-foreground hover:bg-foreground hover:text-background";

  return (
    <div
      data-testid={testId}
      className={`p-8 md:p-10 flex flex-col relative ${containerCls}`}
    >
      {badge && (
        <div className="absolute top-6 right-6 inline-flex items-center gap-1 px-2 py-1 bg-[#C9A85F]/20 border border-[#C9A85F]">
          {badgeIcon}
          <span className="mono text-[9px] uppercase tracking-widest text-[#8A7333]">
            {badge}
          </span>
        </div>
      )}
      <p className={`mono text-[10px] uppercase tracking-[0.25em] flex items-center gap-2 ${eyebrowColor}`}>
        {eyebrowIcon}
        {eyebrow}
      </p>
      <div className="mt-6">
        <span className="wordmark gold-metallic-text text-5xl md:text-6xl">${price}</span>
        <span className="mono ml-2 text-xs uppercase tracking-widest text-muted-foreground">
          / month AUD
        </span>
      </div>
      {priceNote && (
        <p
          data-testid={`${testId}-price-note`}
          className="mt-3 inline-flex w-fit items-center gap-1.5 border border-[#C9A85F] bg-[#C9A85F]/15 px-2.5 py-1 text-xs font-semibold text-[#8A7333]"
        >
          <Sparkles size={11} className="text-[#C9A85F]" />
          {priceNote}
        </p>
      )}
      <p className="mt-3 text-sm text-muted-foreground">{tagline}</p>
      <ul className="mt-8 grid gap-3 flex-grow">
        {features.map((f) => (
          <li key={f} className="flex items-start gap-2 text-sm">
            <Check size={14} className="mt-1 text-[#C9A85F] flex-shrink-0" />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      {extra}
      <div className="mt-10 flex flex-col gap-2">
        <Link
          to={ctaTo}
          data-testid={ctaTestId}
          className={`inline-flex h-12 items-center justify-center gap-2 px-6 text-xs font-semibold uppercase tracking-widest transition-colors ${ctaCls}`}
        >
          {ctaLabel} <ArrowRight size={14} />
        </Link>
        {footNote && (
          <p className="mono text-[10px] text-center text-muted-foreground">
            {footNote}
          </p>
        )}
      </div>
    </div>
  );
}
